import { useEffect, useRef, useState } from 'react';
import './App.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { SplashScreen } from 'layout';
import { HTTP_STATUS, queryKeys, routes, storageKeys } from './constants';
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query';
import {
  detectLineBrowser,
  detectMobile,
  detectPWA,
  getCookies,
  safeStorage,
  trackPwaStandaloneLaunch,
} from 'utils';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactGA from 'react-ga4';
import OneSignal from 'react-onesignal';
import { useSettingStore, useUserStore } from 'store';
import { useAcsKeep, useCache } from 'hooks';
import { AxiosError } from 'axios';
import { getCurrentUser } from 'api';
import { TUser } from 'types';
import { upsertLpTracking } from 'api';
import { setSentryUser } from 'configs/instrument';
import SeoMetadata from 'components/SeoMetadata';
import AgeGatedRoutes from 'routers/AgeGatedRoutes';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failedCount, err) => {
        const continueRetry = failedCount < 2;
        if (err instanceof AxiosError) {
          return err?.status !== HTTP_STATUS.FORBIDDEN && continueRetry;
        }
        return continueRetry;
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  },
});

// Setup api payment
const TRACKING_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
// Setup OneSignal
const APP_ID = process.env.REACT_APP_ONESIGNAL_APP_ID;

function App() {
  const isMobile = detectMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const isSalonRedirect =
    location.pathname === routes.MESSAGE_MANAGEMENT &&
    new URLSearchParams(location.search).has('isSalon');
  const {
    setSubscriptionId,
    setUser,
    setUserRefreshRequested,
    userRefreshRequested,
    isAuthenticated,
    clientId,
  } = useUserStore();

  useCache();
  useAcsKeep();

  const setOpenTutorial = useSettingStore((state) => state.setOpenTutorial);
  const isAppOpenTutorialShown = useRef(false);
  const isStandaloneLaunchTracked = useRef(false);
  const [isConfirmed, setIsConfirmed] = useState<boolean>(
    !!safeStorage.getItem(storageKeys.IS_CONFIRM),
  );
  // Queries
  const {
    data: user,
    isLoading,
    isRefetching,
    refetch: refetchCurrentUser,
  } = useQuery<TUser>({
    queryKey: [queryKeys.CURRENT_USER],
    queryFn: getCurrentUser,
    networkMode: 'always',
    refetchOnMount: false,
    enabled: !!isAuthenticated && !isSalonRedirect,
  });
  const isUserLoading = isLoading && !isSalonRedirect;

  // Effect
  useEffect(() => {
    (async () => {
      if (userRefreshRequested && !isSalonRedirect) {
        await refetchCurrentUser();
        setUserRefreshRequested(false);
      }
    })();
  }, [
    isSalonRedirect,
    location.pathname,
    refetchCurrentUser,
    userRefreshRequested,
    setUserRefreshRequested,
  ]);

  useEffect(() => {
    if (user) {
      setUser(user);
      setSentryUser({
        userId: String(user.id),
        nickname: user.name,
        clientId: clientId || undefined,
      });
      if (
        !isSalonRedirect &&
        user.hasPendingReview &&
        !isRefetching &&
        location.pathname !== routes.REVIEW_GIRL
      ) {
        navigate(routes.REVIEW_GIRL);
      }
    }
  }, [
    isRefetching,
    isSalonRedirect,
    location.pathname,
    navigate,
    setUser,
    user,
    clientId,
  ]);

  useEffect(() => {
    const excludedRoutes = [
      routes.SIGNUP,
      routes.LOGIN,
      routes.LINE_CALLBACK,
      routes.LINE_ADD_FRIEND,
      routes.REVIEW_GIRL,
    ];
    if (
      isAppOpenTutorialShown.current ||
      !isAuthenticated ||
      !isConfirmed ||
      isLoading ||
      isRefetching ||
      !user ||
      user.hasPendingReview ||
      excludedRoutes.some(
        (path) =>
          location.pathname === path ||
          location.pathname.startsWith(`${path}/`),
      ) ||
      localStorage.getItem(storageKeys.IS_CONFIRM_TUTORIAL) ||
      detectPWA() ||
      detectLineBrowser()
    ) {
      return;
    }

    isAppOpenTutorialShown.current = true;
    setOpenTutorial(true, 'app_open');
  }, [
    isAuthenticated,
    isConfirmed,
    isLoading,
    isRefetching,
    location.pathname,
    setOpenTutorial,
    user,
  ]);

  useEffect(() => {
    if (isLoading || isStandaloneLaunchTracked.current || !detectPWA()) return;
    isStandaloneLaunchTracked.current = true;
    trackPwaStandaloneLaunch({ userId: user?.id });
  }, [isLoading, user?.id]);

  useEffect(() => {
    if (
      TRACKING_ID &&
      (process.env.NODE_ENV === 'production' ||
        process.env.REACT_APP_TECO_LOCAL_MODE !== 'true')
    ) {
      ReactGA.initialize(TRACKING_ID);
      // Send pageview with a custom path
      ReactGA.send({
        hitType: 'pageview',
        page: '/',
        title: 'TECO APPLICATION',
      });
    }
  }, []);

  useEffect(() => {
    (async () => {
      const lpTmpMaleId = getCookies('tmp_user_id');
      if (!lpTmpMaleId) return;
      const lpFirstVisitDate = new Date(getCookies('lp_first_visit_date'));
      const lpLastVisitDate = new Date(getCookies('lp_last_visit_date'));
      const lpVisitCount = +getCookies('lp_visit_count');
      const lpReferrerUrl = getCookies('lp_referrer_url') || '';

      await upsertLpTracking({
        lpTmpMaleId,
        maleId: user?.id,
        lpVisitCount,
        lpFirstVisitDate,
        lpLastVisitDate,
        lpReferrerUrl,
      });
    })();
  }, [user]);

  useEffect(() => {
    if (
      APP_ID &&
      (process.env.NODE_ENV === 'production' ||
        process.env.REACT_APP_TECO_LOCAL_MODE !== 'true')
    ) {
      OneSignal.init({
        appId: APP_ID,
        notificationClickHandlerMatch: 'origin',
        notificationClickHandlerAction: 'focus',
        autoRegister: true,
        autoResubscribe: true,
      })
        .then(() => {
          if (detectPWA()) {
            OneSignal.Slidedown.promptPush();
          }
          setSubscriptionId(OneSignal.User.PushSubscription.id || null);
        })
        .catch((err) => {
          console.log(`OneSignal initialization failed: ${err} `);
        });
    } else if (
      process.env.NODE_ENV !== 'production' &&
      process.env.REACT_APP_TECO_LOCAL_MODE === 'true'
    ) {
      setSubscriptionId('local-onesignal-subscription-male');
    }
  }, [setSubscriptionId]);

  useEffect(() => {
    const navigateSpecificPage = (event: MessageEvent<any>) => {
      if (event.data?.action === 'navigate' && event.data?.url) {
        navigate(event.data.url);
      }
    };
    navigator?.serviceWorker?.addEventListener('message', navigateSpecificPage);
    return () => {
      navigator?.serviceWorker?.removeEventListener(
        'message',
        navigateSpecificPage,
      );
    };
  }, [navigate]);

  return (
    <>
      <SeoMetadata />
      <div
        id="app"
        className={`${
          !isMobile ? `w-[50vh] relative mx-auto translate-x-0` : ``
        } h-[100dvh] overflow-hidden bg-white`}
      >
        {isUserLoading ? (
          <SplashScreen />
        ) : (
          <AgeGatedRoutes
            isAgeConfirmed={isConfirmed}
            isLimited={user?.isLimited}
            loading={isUserLoading}
            onConfirm={() => setIsConfirmed(true)}
          />
        )}
        <div id="recaptcha-container" className="invisible"></div>
      </div>
      <ToastContainer
        position="bottom-center"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover={false}
        theme="light"
      />
    </>
  );
}

function withQueryClient(App: () => JSX.Element) {
  return () => (
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  );
}

export default withQueryClient(App);
